"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useEffect } from "react";
import { motion, useSpring, AnimatePresence } from "framer-motion";
const stories = [{
  subtitle: "The Origin",
  title: "Documenting the undocumented.",
  text: "For over a century, the stories of the Girmitiyas, Indian indentured labourers transported across the British, Dutch, and French empires, existed primarily in the margins of colonial ledgers and the oral traditions of their descendants.",
  angle: -45 // Points North-West
}, {
  subtitle: "The Mission",
  title: "Forging a unified historical record.",
  text: "The Global Girmitiya Council was formed out of an urgent necessity to centralize, digitize, and safeguard these fragmented histories. We are a collective of historians, descendants, and archivists working across borders to ensure this legacy is preserved.",
  angle: 45 // Points North-East
}, {
  subtitle: "The Legacy",
  title: "Preserving the journey for descendants.",
  text: "Our mandate is not just preservation, but education. By reconstructing passenger manifests, plantation records, and cultural artifacts, we provide a definitive, accessible resource for descendants tracing their lineage and researchers studying global migration.",
  angle: 135 // Points South-East
}];
const AboutStory = () => {
  const $ = _c(50);
  const compassRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      stiffness: 100,
      damping: 30
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tiltX = useSpring(0, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 100,
      damping: 30
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tiltY = useSpring(0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 120,
      damping: 20
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const needleAngle = useSpring(stories[0].angle, t2);
  let t3;
  let t4;
  if ($[3] !== activeIndex || $[4] !== isHovered || $[5] !== needleAngle) {
    t3 = () => {
      if (!isHovered) {
        needleAngle.set(stories[activeIndex].angle);
      }
    };
    t4 = [activeIndex, isHovered, needleAngle];
    $[3] = activeIndex;
    $[4] = isHovered;
    $[5] = needleAngle;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsHovered(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[9] !== activeIndex || $[10] !== needleAngle || $[11] !== tiltX || $[12] !== tiltY) {
    t6 = () => {
      setIsHovered(false);
      tiltX.set(0);
      tiltY.set(0);
      needleAngle.set(stories[activeIndex].angle);
    };
    $[9] = activeIndex;
    $[10] = needleAngle;
    $[11] = tiltX;
    $[12] = tiltY;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleMouseLeave = t6;
  let t7;
  if ($[14] !== needleAngle || $[15] !== tiltX || $[16] !== tiltY) {
    t7 = e => {
      if (!compassRef.current) {
        return;
      }
      const rect = compassRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      const rotateX = deltaY / (rect.height / 2) * -25;
      const rotateY = deltaX / (rect.width / 2) * 25;
      tiltX.set(rotateX);
      tiltY.set(rotateY);
      const targetAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
      const currentAngle = needleAngle.get();
      const difference = targetAngle - currentAngle;
      const normalizedDiff = Math.atan2(Math.sin(difference * Math.PI / 180), Math.cos(difference * Math.PI / 180)) * (180 / Math.PI);
      needleAngle.set(currentAngle + normalizedDiff);
    };
    $[14] = needleAngle;
    $[15] = tiltX;
    $[16] = tiltY;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleMouseMove = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      perspective: "1200px"
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== tiltX || $[20] !== tiltY) {
    t9 = {
      rotateX: tiltX,
      rotateY: tiltY,
      transformStyle: "preserve-3d"
    };
    $[19] = tiltX;
    $[20] = tiltY;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      rotate: 360
    };
    t11 = {
      duration: 40,
      repeat: Infinity,
      ease: "linear"
    };
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <motion.div animate={t10} transition={t11} className="absolute inset-0 rounded-full border-[1.5px] border-[#DF6D2A]/30 border-dashed" style={{
      transform: "translateZ(10px)"
    }} />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="absolute inset-6 rounded-full border border-[#0F1C2E]/15" style={{
      transform: "translateZ(20px)"
    }} />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40 transition-opacity duration-500 group-hover:opacity-80" viewBox="0 0 200 200" style={{
      transform: "translateZ(30px)"
    }}>{Array.from({
        length: 36
      }).map(_temp)}</svg>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      transform: "translateZ(40px)"
    };
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <svg className="absolute w-[80%] h-[80%] opacity-10" viewBox="0 0 100 100"><polygon points="50,0 55,45 100,50 55,55 50,100 45,55 0,50 45,45" fill="#F4EFE6" /></svg>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== needleAngle) {
    t17 = {
      rotate: needleAngle
    };
    $[29] = needleAngle;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <svg width="40" height="200" viewBox="0 0 40 200" className="h-[140px] sm:h-[200px] md:h-[280px]"><polygon points="20,10 28,100 12,100" fill="#C9A84C" /><polygon points="20,190 28,100 12,100" fill="#DF6D2A" /><circle cx="20" cy="100" r="5" fill="#F4EFE6" /></svg>;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t17) {
    t19 = <div className="absolute inset-12 rounded-full bg-[#0F1C2E] shadow-[0_40px_80px_rgba(15,28,46,0.3)] flex items-center justify-center overflow-hidden" style={t15}>{t16}<motion.div style={t17} className="relative w-full h-full flex items-center justify-center z-10 drop-shadow-2xl">{t18}</motion.div></div>;
    $[32] = t17;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="absolute inset-0 pointer-events-none" style={{
      transform: "translateZ(80px)"
    }}><span className="absolute top-0 left-1/2 -translate-x-1/2 font-display text-[20px] sm:text-[28px] md:text-[32px] text-[#0F1C2E] drop-shadow-md">N</span><span className="absolute bottom-0 left-1/2 -translate-x-1/2 font-display text-[20px] sm:text-[28px] md:text-[32px] text-[#0F1C2E] drop-shadow-md">S</span><span className="absolute left-0 top-1/2 -translate-y-1/2 font-display text-[20px] sm:text-[28px] md:text-[32px] text-[#0F1C2E] drop-shadow-md">W</span><span className="absolute right-0 top-1/2 -translate-y-1/2 font-display text-[20px] sm:text-[28px] md:text-[32px] text-[#0F1C2E] drop-shadow-md">E</span></div>;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== handleMouseLeave || $[36] !== handleMouseMove || $[37] !== t19 || $[38] !== t9) {
    t21 = <motion.div ref={compassRef} onMouseEnter={handleMouseEnter} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={t9} className="relative w-[220px] h-[220px] sm:w-[300px] sm:h-[300px] md:w-[450px] md:h-[450px] cursor-crosshair group">{t12}{t13}{t14}{t19}{t20}</motion.div>;
    $[35] = handleMouseLeave;
    $[36] = handleMouseMove;
    $[37] = t19;
    $[38] = t9;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center pointer-events-none"><span className="font-body text-[9px] tracking-[0.3em] uppercase text-[#0F1C2E]/40">The Compass of Time</span></div>;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t21) {
    t23 = <div className="w-full lg:w-[45%] flex justify-center lg:justify-start"><div style={t8}>{t21}{t22}</div></div>;
    $[41] = t21;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== activeIndex) {
    t24 = stories.map((story, index) => {
      const isActive = activeIndex === index;
      return <div key={index} onClick={() => setActiveIndex(index)} className={`cursor-pointer transition-all duration-500 border-l-2 pl-4 sm:pl-6 md:pl-10 py-2 ${isActive ? "border-[#DF6D2A]" : "border-[#0F1C2E]/15 hover:border-[#DF6D2A]/50"}`}><span className={`font-body text-[10.5px] tracking-[0.25em] uppercase transition-colors duration-300 ${isActive ? "text-[#DF6D2A]" : "text-[#0F1C2E]/40"}`}>{story.subtitle}</span><h2 className={`font-display text-[24px] sm:text-[32px] md:text-[42px] lg:text-[46px] font-light leading-[1.1] mt-2 sm:mt-3 transition-colors duration-300 ${isActive ? "text-[#0F1C2E]" : "text-[#0F1C2E]/40"}`}>{story.title}</h2><AnimatePresence>{isActive && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: "auto",
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
          }} className="overflow-hidden"><p className="font-body text-[14px] sm:text-[15.5px] md:text-[17px] font-light text-[#4C596B] leading-[1.7] sm:leading-[1.8] mt-4 sm:mt-6 max-w-[500px]">{story.text}</p></motion.div>}</AnimatePresence></div>;
    });
    $[43] = activeIndex;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t24) {
    t25 = <div className="w-full lg:w-[55%] flex flex-col gap-4 sm:gap-6">{t24}</div>;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t23 || $[48] !== t25) {
    t26 = <section className="bg-[#F4EFE6] relative z-20 overflow-hidden border-t border-[#0F1C2E]/10 py-16 sm:py-24 md:py-32"><div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 relative z-10"><div className="flex flex-col lg:flex-row items-center gap-10 sm:gap-16 lg:gap-24 relative">{t23}{t25}</div></div></section>;
    $[47] = t23;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  return t26;
};
export default AboutStory;
function _temp(_, i) {
  return <line key={i} x1="100" y1="14" x2="100" y2="18" stroke="#0F1C2E" strokeWidth="0.5" transform={`rotate(${i * 10} 100 100)`} />;
}